import { formatNumber } from '@shared/format';
import { EmptyState } from '@shared/ui/EmptyState';

import { UploadZone } from '../_shared/components';
import { formatEpisodeCount } from '../_shared/constants';
import { toAcceptAttribute, type useUploadPolicy } from '../_shared/queries';
import type { Group, MediaAsset } from '../_shared/types';
import { ArtworkControl } from './ContentAssets.ArtworkControl';
import { AssetRow } from './ContentAssets.AssetRow';
import { copy } from './copy';

/**
 * The series shape — seasons, each with its episodes and **its own upload zone**.
 *
 * The season chip is a shortcut over its episodes: it presses every one of them,
 * and it reads its own state back from them, so a season shown as free with one
 * episode still behind the wall cannot happen. A season holding **no** episodes
 * has its chip disabled with the reason beside it — the entitlement lands on an
 * asset, and an empty season marked free opens nothing.
 */
export function SeriesPane({
  groups,
  episodes,
  uploadPolicy,
  videoAccept,
  busyDocument,
  uploadProgress,
  uploadErrors,
  onAddSeason,
  onEditGroup,
  onRemoveGroup,
  onUploadEpisode,
  onEditAsset,
  onRemoveAsset,
  onMoveAsset,
  onToggleFree,
  onAddDocument,
  onAddSeasonDocument,
  onRemoveDocument,
}: {
  groups: Group[];
  episodes: MediaAsset[];
  uploadPolicy: ReturnType<typeof useUploadPolicy>['data'];
  videoAccept: string;
  busyDocument: boolean;
  /** Fractional progress per season id — a season uploads independently of its siblings. */
  uploadProgress: Record<string, number | null>;
  /** Refusal per season id, drawn under the season's own zone. */
  uploadErrors: Record<string, string | null>;
  onAddSeason: () => void;
  onEditGroup: (group: Group) => void;
  onRemoveGroup: (group: Group, episodeCount: number) => void;
  onUploadEpisode: (groupId: string, files: FileList) => void;
  onEditAsset: (asset: MediaAsset) => void;
  onRemoveAsset: (asset: MediaAsset) => void;
  onMoveAsset: (list: MediaAsset[], asset: MediaAsset, direction: 'up' | 'down') => void;
  onToggleFree: (asset: MediaAsset) => void;
  onAddDocument: (subjectId: string, file: File) => void;
  /** A season-wide track attaches to the **season**, not to one of its episodes. */
  onAddSeasonDocument: (groupId: string, file: File) => void;
  onRemoveDocument: (documentId: string, label: string, subjectId: string, owner: string) => void;
}) {
  return (
    <section aria-labelledby="assets-seasons" className="mb-8 grid gap-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <h2 id="assets-seasons" className="font-display text-h2 font-bold">
          {copy.seasons.heading}
        </h2>
        <button
          type="button"
          onClick={onAddSeason}
          className="hit-target ms-auto rounded-sm border border-border-strong bg-surface-raised px-4 text-sm font-semibold text-ink hover:bg-surface-overlay"
        >
          {copy.seasons.add}
        </button>
      </div>

      <p className="mb-0 max-w-measure text-xs text-ink-muted">{copy.seasons.lede}</p>

      {groups.length === 0 ? <EmptyState message={copy.seasons.empty} /> : null}

      {groups.map((group, groupIndex) => {
        const seasonEpisodes = episodes.filter((asset) => asset.group_id === group.id);
        const allFree = seasonEpisodes.length > 0 && seasonEpisodes.every((asset) => asset.is_free_preview);

        return (
          <section
            key={group.id}
            aria-labelledby={`season-${group.id}`}
            className="grid gap-3 rounded-md border border-border bg-bg-sunken p-3"
          >
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <b className="text-sm [font-variant-numeric:tabular-nums]">
                <bdi>{formatNumber(groupIndex + 1)}</bdi>
              </b>

              <h3 id={`season-${group.id}`} className="mb-0 grow font-display text-h4 font-bold">
                {group.title}
              </h3>

              <span className="text-xs text-ink-muted">
                <bdi>{formatEpisodeCount(seasonEpisodes.length)}</bdi>
              </span>

              <button
                type="button"
                aria-pressed={allFree}
                disabled={seasonEpisodes.length === 0}
                onClick={() => seasonEpisodes.forEach((asset) => onToggleFree(asset))}
                className="hit-target rounded-pill border border-border bg-surface-raised px-3 text-xs text-ink-muted hover:bg-hover-bg disabled:cursor-not-allowed disabled:opacity-45"
              >
                {copy.seasons.freeSeason}
              </button>

              <button
                type="button"
                onClick={() => onEditGroup(group)}
                className="hit-target rounded-sm px-3 text-xs font-semibold text-ink hover:bg-hover-bg"
              >
                {copy.seasons.edit}
              </button>

              {/*
                Quiet in rest: a ghost icon that takes the danger colour only on
                hover and focus. A permanently red control on every season turns
                the page into a continuous warning, and a warning that is always
                on is not read — the dialog is where this action gets loud.
              */}
              <button
                type="button"
                aria-label={copy.seasons.remove(group.title)}
                onClick={() => onRemoveGroup(group, seasonEpisodes.length)}
                className="hit-target flex items-center justify-center rounded-sm text-ink-muted hover:bg-danger-soft hover:text-danger"
              >
                <svg
                  viewBox="0 0 24 24"
                  width="1.125em"
                  height="1.125em"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path d="M4 7h16" />
                  <path d="M9.5 7V5.5A1.5 1.5 0 0111 4h2a1.5 1.5 0 011.5 1.5V7" />
                  <path d="M6.5 7l.8 12a1.5 1.5 0 001.5 1.4h6.4a1.5 1.5 0 001.5-1.4l.8-12" />
                </svg>
              </button>
            </div>

            {/* A disabled control always explains itself in adjacent text. */}
            {seasonEpisodes.length === 0 ? (
              <p className="mb-0 max-w-measure text-xs text-ink-muted">{copy.seasons.freeSeasonDisabled}</p>
            ) : null}

            {group.description ? (
              <p className="mb-0 max-w-measure text-xs text-ink-muted">{group.description}</p>
            ) : null}

            {/*
              A season is illustrated independently of its parent — the catalog
              says so and `GROUP: thumbnail:required` is how the service states
              it. The control sits above the episodes because it describes the
              season, not any one of them.
            */}
            <ArtworkControl
              subjectType="GROUP"
              subjectId={group.id}
              subjectName={group.title}
              uploadPolicy={uploadPolicy}
            />

            {seasonEpisodes.length === 0 ? (
              <EmptyState message={copy.seasons.emptySeason} />
            ) : (
              <ul className="grid list-none gap-3 p-0">
                {seasonEpisodes.map((asset, index) => (
                  <AssetRow
                    key={asset.id}
                    asset={asset}
                    uploadPolicy={uploadPolicy}
                    ordinal={index + 1}
                    canReorder
                    freeControl="chip"
                    busyDocument={busyDocument}
                    onEdit={() => onEditAsset(asset)}
                    onRemove={() => onRemoveAsset(asset)}
                    onMove={(direction) => onMoveAsset(seasonEpisodes, asset, direction)}
                    onToggleFree={() => onToggleFree(asset)}
                    onAddDocument={(file) => onAddDocument(asset.id, file)}
                    onRemoveDocument={(documentId, label) =>
                      onRemoveDocument(documentId, label, asset.id, asset.title)
                    }
                  />
                ))}
              </ul>
            )}

            <UploadZone
              compact
              label={copy.seasons.dropLabel(group.title)}
              accept={videoAccept}
              hint={copy.seasons.dropHint(group.title)}
              progress={uploadProgress[group.id] ?? null}
              error={uploadErrors[group.id] ?? null}
              onFiles={(files) => onUploadEpisode(group.id, files)}
            />

            {/* A season-wide track is rare, so it is a line rather than a block. */}
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-xs text-ink-muted">{copy.seasons.subtitleLead(group.title)}</span>
              <label className="hit-target inline-flex cursor-pointer items-center rounded-sm px-3 text-xs font-semibold text-ink hover:bg-hover-bg">
                {copy.seasons.subtitleAdd}
                <input
                  type="file"
                  className="sr-only"
                  accept={toAcceptAttribute(uploadPolicy?.accepted_document_formats)}
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) onAddSeasonDocument(group.id, file);
                    event.target.value = '';
                  }}
                />
              </label>
            </div>
          </section>
        );
      })}
    </section>
  );
}
